/**
 * Single source of truth for every threshold + label used by the
 * Admin Analytics dashboard. Edit numbers/labels here — the template
 * and component pick them up automatically.
 *
 * Keeping this in one place ensures the UI stays in sync with the
 * client-facing documentation (ADMIN_ANALYTICS.md).
 */

// ── Content Type — Avg Completion ─────────────────────────────────────────
export const CONTENT_TYPE_COMPLETION_THRESHOLDS = {
  STRONG: 70,   // ≥ 70 → Strong
  AVERAGE: 45,  // 45–69 → Average ; < 45 → Low
};

export type ContentTypeStatus = 'Strong' | 'Average' | 'Low';
export type StatusTier        = 'top' | 'mid' | 'low';

export function contentTypeStatus(avgCompletion: number): ContentTypeStatus {
  if (avgCompletion >= CONTENT_TYPE_COMPLETION_THRESHOLDS.STRONG)  { return 'Strong'; }
  if (avgCompletion >= CONTENT_TYPE_COMPLETION_THRESHOLDS.AVERAGE) { return 'Average'; }
  return 'Low';
}

export function contentTypeTier(avgCompletion: number): StatusTier {
  if (avgCompletion >= CONTENT_TYPE_COMPLETION_THRESHOLDS.STRONG)  { return 'top'; }
  if (avgCompletion >= CONTENT_TYPE_COMPLETION_THRESHOLDS.AVERAGE) { return 'mid'; }
  return 'low';
}

// ── Content Type — Rating Colour ──────────────────────────────────────────
export const CONTENT_TYPE_RATING_THRESHOLDS = {
  TOP: 4,   // ≥ 4   → green
  MID: 3,   // 3–3.9 → amber ; < 3 → red
};

export function ratingTier(avgRating: number): StatusTier {
  if (avgRating >= CONTENT_TYPE_RATING_THRESHOLDS.TOP) { return 'top'; }
  if (avgRating >= CONTENT_TYPE_RATING_THRESHOLDS.MID) { return 'mid'; }
  return 'low';
}

// ── Feature Utilization ───────────────────────────────────────────────────
export const FEATURE_UTILIZATION_THRESHOLDS = {
  HEALTHY: 70,   // ≥ 70 → Healthy
  MODERATE: 30,  // 30–69 → Moderate ; < 30 → Unused
};

export type FeatureStatus = 'Healthy' | 'Moderate' | 'Unused';

export function featureStatus(utilizationPct: number): FeatureStatus {
  if (utilizationPct >= FEATURE_UTILIZATION_THRESHOLDS.HEALTHY)  { return 'Healthy'; }
  if (utilizationPct >= FEATURE_UTILIZATION_THRESHOLDS.MODERATE) { return 'Moderate'; }
  return 'Unused';
}

export function featureTier(utilizationPct: number): StatusTier {
  if (utilizationPct >= FEATURE_UTILIZATION_THRESHOLDS.HEALTHY)  { return 'top'; }
  if (utilizationPct >= FEATURE_UTILIZATION_THRESHOLDS.MODERATE) { return 'mid'; }
  return 'low';
}

// Threshold below which a feature is shown in the "Underused Features"
// callout under the Feature Utilization table.
export const UNDERUSED_FEATURE_THRESHOLD = FEATURE_UTILIZATION_THRESHOLDS.MODERATE;
